/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { SimpleText } from '@msdyn365-commerce-modules/data-types';
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import { observer } from 'mobx-react';
import * as React from 'react';
import CallToAction from '../../components/call-to-action';
import { ISmweBreadcrumbData } from './smwe-breadcrumb.data';
import { ISmweBreadcrumbProps } from './smwe-breadcrumb.props.autogenerated';

/**
 *
 * Breadcrumb component
 * @extends {React.Component<ISmweBreadcrumbProps<ISmweBreadcrumbData>>}
 */
@observer
class Breadcrumb extends React.Component<ISmweBreadcrumbProps<ISmweBreadcrumbData>> {

  public render(): JSX.Element | null {
    // This should never occur
    if (!(this.props && this.props.data && this.props.config)) {
      return null;
    }
    // Contruct the text for the current location (currently pulls either the category or product name)
    let breadcrumbData;
    let categoryData;
    if (this.props.data.breadcrumbData) {
      breadcrumbData = this.props.data.breadcrumbData.result;
      if (breadcrumbData) {
        categoryData = this._getCategory();
      } else if (this.props.config.allWinesCategoryId) {
        // waits for data actions on plp and pdp pages
        return null;
      }
    }
    return this._renderBreadcrumbs(categoryData, breadcrumbData);
  }
  // =========================================================================
  // PRIVATE METHODS
  // =========================================================================

  // ------------------------------------------------------
  // Returns parent category based off url path
  // ------------------------------------------------------
  private _getCategory(): CategoryHierarchy | undefined {
    if (!this.props.data.categories) { return; }
    const categories = this.props.data.categories.result;
    if (!categories) { return; }
    const currentUrl = window.location.pathname;
    let searchedCategory = {} as CategoryHierarchy;
    categories.forEach(category => {
      if (category.Children) {
        searchedCategory = category.Children.find(child => (child.Slug && currentUrl.includes(child.Slug))) || searchedCategory;
      }
    });
    return searchedCategory;
  }

  // ------------------------------------------------------
  // ------------------------------------------------------
  private _isAllWines(categoryData: CategoryHierarchy): boolean {
    if (!this.props.config.allWinesCategoryId) { return false; }
    return (categoryData.RecordId === this.props.config.allWinesCategoryId && categoryData.Url && categoryData.Url.includes(window.location.pathname)) ? true : false;
  }

  // ------------------------------------------------------
  // ------------------------------------------------------
  private _getParentBreadcrumbText(isAllWinesPage: boolean): string {
    const { config } = this.props;
    if (isAllWinesPage && config.allWinesBreadcrumbLink && config.allWinesBreadcrumbLink.linkText) {
      return config.allWinesBreadcrumbLink.linkText;
    } else {
      return config.staticBreadcrumbItemText || 'Home';
    }
  }

  // ------------------------------------------------------
  // ------------------------------------------------------
  private _getParentBreadcrumbLink(isAllWinesPage: boolean): string {
    const { config } = this.props;
    if (isAllWinesPage && config.allWinesBreadcrumbLink && config.allWinesBreadcrumbLink.linkUrl && config.allWinesBreadcrumbLink.linkUrl.destinationUrl) {
      return config.allWinesBreadcrumbLink.linkUrl.destinationUrl;
    } else {
      const link = config.staticBreadcrumbItemLink;
      return (link && link.linkUrl && link.linkUrl.destinationUrl) || '/';
    }
  }

  // ------------------------------------------------------
  // ------------------------------------------------------
  private _renderBreadcrumbs(categoryData: CategoryHierarchy | undefined, breadcrumbData: SimpleText | undefined): JSX.Element {
    const { config } = this.props;
    const separator = config.breadcrumbSeparator || '|';
    const isAllWinesPage = categoryData ? this._isAllWines(categoryData) : false;

    // Pull values from config for breadcrumb item links
    const staticText = this._getParentBreadcrumbText(isAllWinesPage);
    const link = config.staticBreadcrumbItemLevel2Link;
    const lastBreadcrumbLink = (link && link.linkUrl && link.linkUrl.destinationUrl) || '';
    const lastBreadcrumbText = (breadcrumbData && breadcrumbData.text) || (config.staticBreadcrumbItemLevel2Text || '');

    return (
      <div className='ms-breadcrumb' aria-label='Breadcrumb' role='navigation'>
        {staticText && (
          <CallToAction
            className='ms-breadcrumb-cta'
            title={staticText}
            color='link'
            href={this._getParentBreadcrumbLink(isAllWinesPage)}
          />
        )}

        {(categoryData && categoryData.Url && !categoryData.Url.includes(window.location.pathname) && categoryData.RecordId !== config.allWinesCategoryId) && (
          <div className='ms-breadcrumb-data'>
            <div className='ms-breadcrumb-data-separator' aria-hidden='true'>{separator}</div>
            <CallToAction
              className='ms-breadcrumb-cta'
              title={categoryData.Name}
              color='link'
              href={categoryData.Url}
            />
          </div>
        )}

        {lastBreadcrumbText && (
          <div className='ms-breadcrumb-data'>
            <div className='ms-breadcrumb-data-separator' aria-hidden='true'>{separator}</div>
            {lastBreadcrumbLink && (
              <CallToAction
                className='ms-breadcrumb-cta'
                title={lastBreadcrumbText}
                color='link'
                href={lastBreadcrumbLink}
                aria-current='page'
              />
            ) || (
              <div className='ms-breadcrumb-data-text' aria-current='page'>{lastBreadcrumbText}</div>
              )}
          </div>
        )}
      </div>
    );
  }
}

export default Breadcrumb;
